import { useEffect, useState } from "react";
import Icon from "./Icon";

const TYPE_CONFIG = {
    information: null,
    success: { icon: "check-circle", className: "text-main-icon-accented-success" },
    error: { icon: "x-circle", className: "text-main-icon-accented-error" },
};

const DEFAULT_AUTO_HIDE_DURATION = 3000;
const FADE_OUT_DURATION = 300;

/**
 * Toast component
 * 
 * Renders a toast notification with optional icon and close button.
 *
 * @param {object} props
 * @param {"information"|"success"|"error"} [props.type="information"]
 *  Type of toast notification.
 * 
 * @param {string} props.message
 *   Text content displayed inside the tooltip container.
 * 
 * @param {function} [props.onClose]
 *   Optional callback function triggered when the toast is closed.
 * 
 * @param {boolean} [props.autohide=true]
 *   Whether the toast auto hides after the given duration.
 * 
 * @param {number} [props.duration=3000]
 *   Time in milliseconds before auto-hiding when `autohide` is true.
 * 
 * @param {boolean} [props.showCloseButton]
 *   Force showing or hiding the close button. Defaults to true when undefined.
 * 
 * @param {boolean} [props.fullWidth=false]
 *   Whether the toast should stretch to 100% width of its container.
 * 
 * @param {string} [props.maxWidth="560px"]
 *   Maximum width when `fullWidth` is false.
 * 
 * @param {string} [props.minWidth="320px"]
 *   Minimum width applied in both width modes.
 * 
 */
export default function Toast({
    type = "information",
    message,
    onClose,
    autohide = true,
    duration = DEFAULT_AUTO_HIDE_DURATION,
    showCloseButton,
    fullWidth = false,
    maxWidth = "560px",
    minWidth = "320px",
}) {
    const cfg = TYPE_CONFIG[type] || null;
    const [visible, setVisible] = useState(true);
    const [mounted, setMounted] = useState(true);

    const shouldShowClose = typeof showCloseButton === "boolean" ? showCloseButton : true;

    useEffect(() => {
        if (!autohide) return;
        const hideAfter = Number.isFinite(duration) ? duration : DEFAULT_AUTO_HIDE_DURATION;
        const timer = setTimeout(() => {
            setVisible(false);
        }, hideAfter);
        return () => clearTimeout(timer);
    }, [autohide, duration]);

    useEffect(() => {
        if (visible) return;
        const timer = setTimeout(() => {
            setMounted(false);
            onClose && onClose();
        }, FADE_OUT_DURATION);
        return () => clearTimeout(timer);
    }, [visible, onClose]);

    const handleClose = () => {
        if (!visible) return;
        setVisible(false);
    };

    function buildIcon() {
        return (
            <Icon name={cfg.icon} size="md" className={cfg.className} />
        );
    }

    function buildCloseButton() {
        if (!shouldShowClose) return null;
        return (
            <button
                type="button"
                className="inline-flex items-center justify-center"
                onClick={handleClose}
                aria-label="Close"
            >
                <Icon name="x" size="md" className="text-creation-neutral-solid-on-surface-default" />
            </button>
        );
    }

    if (!mounted) return null;

    const widthStyle = fullWidth
        ? { width: "100%", maxWidth: "100%", minWidth: minWidth || undefined }
        : { width: "fit-content", maxWidth: maxWidth || undefined, minWidth: minWidth || undefined };

    return (
        <div
            role="alert"
            className={`toast transition-opacity duration-300 ease-in-out ${visible ? "opacity-100" : "opacity-0"}`}
            style={widthStyle}
        >
            {cfg && buildIcon()}
            <div className="toast-message">{message}</div>
            {buildCloseButton()}
        </div>
    );
}
